import React, { useCallback, useState } from "react";
import MenuIcon from "@mui/icons-material/Menu";
import "./App.css";
import CssBaseline from "@mui/material/CssBaseline";
import {
  AppBar,
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  SwipeableDrawer,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Typography,
  useTheme,
} from "@mui/material";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  DatePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTime } from "luxon";
import {
  Outlet,
  useNavigate,
  useRouteLoaderData,
  useSearchParams,
} from "react-router-dom";
import { AppContext } from "./app-context";
import {
  ALL_VERSIONS,
  LoaderData,
  Version,
  isVersionEnabled,
  prettyPrintVersion,
  sortVersions,
} from "./types";
import { formatMonthAndDay, toDateTime, nextDate, previousDate } from "./util";

export default function App() {
  const { date, versions } = useRouteLoaderData("root") as LoaderData;
  const datetime = toDateTime(date);

  const [queryParams] = useSearchParams();
  const navigate = useNavigate();

  const handleDateChange = useCallback(
    (newDate: DateTime) => {
      const dateString = formatMonthAndDay(newDate);
      queryParams.set("date", dateString);
      navigate(`?${queryParams.toString()}`);
    },
    [navigate, queryParams]
  );

  const handleVersionChange = useCallback(
    (event: React.MouseEvent<HTMLElement>, newVersions: Version[]) => {
      queryParams.delete("version");
      if (newVersions.length === 0) {
        queryParams.set("version", "none");
      } else {
        newVersions.forEach((v) => {
          queryParams.append("version", v);
        });
      }
      navigate(`?${queryParams.toString()}`);
    },
    [navigate, queryParams]
  );

  const [drawerOpen, setDrawerOpen] = useState(false);

  const [showTwoColumns, setShowTwoColumns] = useState(true);
  const [showOriginal, setShowOriginal] = useState(true);
  const [showNew, setShowNew] = useState(true);
  const toggleView = (view: "original" | "new") => () => {
    const show = view === "original" ? !showOriginal : !showNew;
    // Show at least one view at all times
    if (view === "original" && !show && !showNew) {
      setShowNew(true);
      setShowOriginal(false);
    } else if (view === "new" && !show && !showOriginal) {
      setShowOriginal(true);
      setShowNew(false);
    } else {
      if (view === "original") {
        setShowOriginal(show);
      } else {
        setShowNew(show);
      }
    }
  };

  const theme = useTheme();

  const enabledVersions = sortVersions(
    ALL_VERSIONS.filter((v) => v !== "original" && isVersionEnabled(v))
  );

  const onNextDateButtonClick = useCallback(() => {
    const newDate = nextDate(date);
    const dateString = formatMonthAndDay(toDateTime(newDate));
    queryParams.set("date", dateString);
    navigate(`?${queryParams.toString()}`);
  }, [date, navigate, queryParams]);
  const onPreviousDateButtonClick = useCallback(() => {
    const newDate = previousDate(date);
    const dateString = formatMonthAndDay(toDateTime(newDate));
    queryParams.set("date", dateString);
    navigate(`?${queryParams.toString()}`);
  }, [date, navigate, queryParams]);

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <CssBaseline />
      <SwipeableDrawer
        anchor="left"
        open={drawerOpen}
        onOpen={() => setDrawerOpen(true)}
        onClose={() => setDrawerOpen(false)}
        swipeAreaWidth={20}
      >
        <Box
          display="flex"
          flexDirection="column"
          gap={4}
          p={2}
          maxWidth="80vw"
          minWidth="300px"
        >
          <FormControlLabel
            control={
              <Switch
                checked={showTwoColumns}
                onChange={(e, checked) => setShowTwoColumns(checked)}
              />
            }
            label="Two columns?"
            color={theme.palette.grey[800]}
          />
          <Box display="flex">
            <ToggleButton
              value="original"
              selected={showOriginal}
              onChange={toggleView("original")}
              sx={{
                width: "50%",
              }}
            >
              Original JFT
            </ToggleButton>
            <ToggleButton
              value="new"
              selected={showNew}
              onChange={toggleView("new")}
              sx={{
                width: "50%",
              }}
            >
              Alt JFT
            </ToggleButton>
          </Box>
          <Divider />
          <Box>
            <Typography variant="body2" color={theme.palette.grey[800]}>
              JFT versions to show
            </Typography>
            <ToggleButtonGroup
              value={versions}
              onChange={handleVersionChange}
              orientation="vertical"
              aria-label="Versions to show"
            >
              {enabledVersions.map((version) => (
                <ToggleButton
                  key={version}
                  value={version}
                  sx={{ justifyContent: "start" }}
                >
                  {prettyPrintVersion(version)}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        </Box>
      </SwipeableDrawer>
      <Box display="flex" flexDirection="column" height="100vh">
        <AppBar position="static">
          <Toolbar
            sx={{
              gap: 2,
            }}
          >
            <IconButton
              size="large"
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={() => setDrawerOpen(!drawerOpen)}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Just For Today
            </Typography>
            <DatePicker
              sx={{
                "& .MuiInputBase-root": {
                  backgroundColor: "white",
                },
                width: "200px",
              }}
              views={["month", "day"]}
              value={datetime}
              onChange={(newDate) => {
                if (newDate) {
                  handleDateChange(newDate);
                }
              }}
            />
          </Toolbar>
        </AppBar>
        <AppContext.Provider
          value={{
            selectedVersions: versions,
            showOriginal,
            showNew,
            showTwoColumns,
          }}
        >
          <Outlet />
          <Paper elevation={3}>
            <BottomNavigation
              sx={{
                width: "100%",
                justifyContent: "space-between",
                borderTop: "1px solid #e0e0e0",
              }}
            >
              <BottomNavigationAction
                label="Previous"
                value="previous"
                icon={<ArrowLeftIcon />}
                onClick={onPreviousDateButtonClick}
              />
              <BottomNavigationAction
                label="Next"
                value="next"
                icon={<ArrowRightIcon />}
                onClick={onNextDateButtonClick}
              />
            </BottomNavigation>
          </Paper>
        </AppContext.Provider>
      </Box>
    </LocalizationProvider>
  );
}
